/**
 * Assignment Status Badge
 *
 * Displays assignment indicator with deadline and status color.
 * Shows "Assigned - Due {date}" with appropriate status color.
 */

import { Box, Typography } from '../ui';
import { AssignmentStatus } from '../../apis/api-client';

interface AssignmentStatusBadgeProps {
  assignmentStatus: AssignmentStatus;
  deadline?: Date;
  sx?: any;
}

// Map AssignmentStatus to colors per requirements
const STATUS_COLORS: Record<AssignmentStatus, string> = {
  [AssignmentStatus.Upcoming]: '#2196f3',   // blue
  [AssignmentStatus.Active]: '#4caf50',     // green
  [AssignmentStatus.Overdue]: '#f44336',    // red
  [AssignmentStatus.Completed]: '#9e9e9e',  // grey
};

export default function AssignmentStatusBadge({
  assignmentStatus,
  deadline,
  sx,
}: AssignmentStatusBadgeProps) {
  const statusColor = STATUS_COLORS[assignmentStatus];
  const formattedDeadline = deadline
    ? new Date(deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : 'No deadline';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        ...sx,
      }}
    >
      {/* Status indicator dot */}
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: statusColor,
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: statusColor,
          fontWeight: 500,
        }}
      >
        Assigned - Due {formattedDeadline}
      </Typography>
    </Box>
  );
}
